import React, { KeyboardEvent, RefObject } from "react";

import { ClickAwayListener, Grow, MenuList, Paper, Popper, useTheme } from "@mui/material";

export interface DropdownListProps {
    isOpen: boolean;
    onClickAway?: () => void;
    isBlockedOnClickAway?: boolean;
    anchorRef: RefObject<HTMLButtonElement | null>;
    children?: React.ReactNode;
    eventType?: "onKeyDown" | "onClick";
    displayLeft?: boolean;
}

export function DropdownList({
    children,
    isOpen,
    onClickAway,
    isBlockedOnClickAway,
    anchorRef,
    eventType = "onClick",
    displayLeft = false,
}: DropdownListProps): React.JSX.Element {
    const theme = useTheme();

    const handleClose = () => {
        if (isBlockedOnClickAway) {
            return;
        }
        if (onClickAway) {
            onClickAway();
        }
    };

    const handleListKeyDown = (event: KeyboardEvent) => {
        if (isBlockedOnClickAway && event.key === "Tab") {
            event.preventDefault();
            if (onClickAway) {
                onClickAway();
            }
        }
    };

    const handleListClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (onClickAway) {
            onClickAway();
        }
    };

    const eventHandlers = eventType === "onKeyDown" ? { onKeyDown: handleListKeyDown } : { onClick: handleListClick };
    const placementSettings = displayLeft ? "left-end" : "bottom-end";
    const modifiers = displayLeft
        ? [
              {
                  name: "offset",
                  options: {
                      offset: [12, 0],
                  },
              },
          ]
        : [
              {
                  name: "preventOverflow",
                  enabled: true,
                  options: {
                      altAxis: true,
                      padding: 8,
                      boundary: "viewport",
                  },
              },
              {
                  name: "flip",
                  enabled: true,
                  options: {
                      padding: 8,
                  },
              },
          ];

    return (
        <Popper
            open={isOpen && !!anchorRef.current}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal={false}
            placement={placementSettings}
            modifiers={modifiers}
            style={{ zIndex: 1300 }}
        >
            {({ TransitionProps, placement }) => (
                <Grow
                    {...TransitionProps}
                    style={{
                        marginTop: theme.spacing(1),
                        transformOrigin: placement === "bottom" ? "right bottom" : "right top",
                    }}
                >
                    <Paper elevation={8} sx={{ zIndex: 1300 }}>
                        <ClickAwayListener onClickAway={handleClose}>
                            <MenuList id="menu-list-grow" {...eventHandlers} style={{ display: "flex", flexDirection: "column" }}>
                                {children}
                            </MenuList>
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
        </Popper>
    );
}
